import React from 'react';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import {
    MapPin,
    Lock,
    Bell,
    DollarSign,
    Calendar,
    BarChart,
    Clock,
    Users,
    Award,
    Check,
} from 'lucide-react';

// 引入首頁組件
import HeroBanner from '../components/home/HeroBanner';
import FeatureCard from '../components/common/FeatureCard';
import ModuleCard from '../components/home/ModuleCard';
import PricingCard from '../components/home/PricingCard';
import TestimonialCard from '../components/home/TestimonialCard';

const HomePage = () => {
    // 使用 useInView 來實現滾動動畫
    const [featuresRef, featuresInView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const [modulesRef, modulesInView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const [pricingRef, pricingInView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    // 特色功能數據
    const features = [
        {
            icon: <MapPin size={28} />,
            title: 'GPS 定位打卡',
            description: '確保員工於指定範圍內簽到，防止異地打卡，提高出勤管理精確度。'
        },
        {
            icon: <Lock size={28} />,
            title: '防偽打卡機制',
            description: '採用 FingerprintJS 裝置指紋技術，有效防止代打卡行為，確保資料真實性。'
        },
        {
            icon: <Bell size={28} />,
            title: '即時通知提醒',
            description: '異常打卡、請假審核等重要事件即時通知，讓管理零延遲。'
        },
        {
            icon: <DollarSign size={28} />,
            title: '自動薪資計算',
            description: '整合出勤、加班、請假數據，自動計算薪資，減少人為錯誤。'
        },
        {
            icon: <Calendar size={28} />,
            title: '智能排班系統',
            description: '根據人力需求自動分配最優班次，提高資源利用效率。'
        },
        {
            icon: <BarChart size={28} />,
            title: '數據分析報表',
            description: '豐富的分析報表，助您掌握企業人力資源概況，做出明智決策。'
        }
    ];

    // 系統模組數據
    const modules = [
        {
            icon: <Clock size={28} />,
            title: '基礎出勤管理',
            description: '通過 GPS 定位和防偽機制，實現精準的員工考勤管理，防止異地打卡和代打卡。',
            features: [
                'GPS 簽到簽退',
                '設定打卡事由',
                'FingerprintJS 防偽',
                '異常即時警示'
            ]
        },
        {
            icon: <DollarSign size={28} />,
            title: '薪資與假勤管理',
            description: '全自動計算薪資，整合假勤系統，提高人資部門效率，減少人為錯誤。',
            features: [
                '自動計算薪資',
                '假別彈性管理',
                '離職結算計算',
                '薪資結構自訂'
            ]
        },
        {
            icon: <Award size={28} />,
            title: '績效與考核管理',
            description: '建立透明的績效評估體系，連結績效與薪酬，促進員工成長與組織發展。',
            features: [
                'KPI 設定管理',
                '主管評分系統',
                '自動計算考核',
                '績效分析報表'
            ]
        },
        {
            icon: <Calendar size={28} />,
            title: '排班與工時管理',
            description: '智能排班系統自動安排最佳人力配置，提高資源利用效率，降低管理成本。',
            features: [
                '多班別彈性設定',
                '智能排班系統',
                '工時記錄整合',
                '班表個人查詢'
            ]
        }
    ];

    // 價格方案數據
    const pricingPlans = [
        {
            name: '標準版',
            price: 'NT$1,500',
            target: '小型企業',
            employees: '1~50名員工',
            modules: ['基礎出勤管理模組', '排班管理模組'],
            highlight: false
        },
        {
            name: '進階版',
            price: 'NT$3,500',
            target: '中型企業',
            employees: '51~200名員工',
            modules: ['基礎出勤管理模組', '排班管理模組', '假勤與薪資管理模組', '通知模組'],
            highlight: true
        },
        {
            name: '專業版',
            price: 'NT$6,000',
            target: '大型企業',
            employees: '201~500名員工',
            modules: ['所有模組（8大功能模組）', '優先技術支援', '專屬客服經理'],
            highlight: false
        }
    ];

    // 客戶見證
    const testimonials = [
        {
            name: '林經理',
            company: '台北科技有限公司',
            quote: '導入系統後，人資部門工作效率提升了30%，大幅減少薪資計算錯誤。'
        },
        {
            name: '張董事長',
            company: '新竹精密製造',
            quote: '智能排班功能為我們節省了大量人力資源成本，員工滿意度也明顯提高。'
        },
        {
            name: '王主任',
            company: '高雄服務業集團',
            quote: 'GPS打卡功能徹底解決了我們的異地打卡問題，系統穩定且易於使用。'
        }
    ];

    return (
        <div>
            {/* 英雄區塊 */}
            <HeroBanner />

            {/* 特色區塊 */}
            <section id="features" className="section bg-white">
                <div className="container">
                    <h2 className="section-title">為什麼選擇我們？</h2>
                    <p className="section-subtitle">
                        解決企業人事管理痛點，提升效率降低成本
                    </p>

                    <div
                        ref={featuresRef}
                        className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 ${featuresInView ? 'animate-on-scroll in-view' : 'animate-on-scroll'
                            }`}
                    >
                        {features.map((feature, index) => (
                            <FeatureCard
                                key={index}
                                icon={feature.icon}
                                title={feature.title}
                                description={feature.description}
                                delay={index * 0.1}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* 模組介紹 */}
            <section id="modules" className="section bg-gray-50">
                <div className="container">
                    <h2 className="section-title">系統模組</h2>
                    <p className="section-subtitle">
                        企業可依需求自由選擇以下模組，彈性組合最適合的方案
                    </p>

                    <div
                        ref={modulesRef}
                        className={`grid grid-cols-1 md:grid-cols-2 gap-8 ${modulesInView ? 'animate-on-scroll in-view' : 'animate-on-scroll'
                            }`}
                    >
                        {modules.map((module, index) => (
                            <ModuleCard
                                key={index}
                                icon={module.icon}
                                title={module.title}
                                description={module.description}
                                features={module.features}
                                delay={index * 0.1}
                            />
                        ))}
                    </div>

                    <div className="text-center mt-12">
                        <Link to="/features" className="btn btn-primary">
                            查看完整模組與功能
                        </Link>
                    </div>
                </div>
            </section>

            {/* 定價方案 */}
            <section id="pricing" className="section bg-white">
                <div className="container">
                    <h2 className="section-title">方案價格</h2>
                    <p className="section-subtitle">
                        彈性選擇適合企業規模的訂閱方案，按月付費無需大額投資
                    </p>

                    <div
                        ref={pricingRef}
                        className={`grid grid-cols-1 md:grid-cols-3 gap-8 ${pricingInView ? 'animate-on-scroll in-view' : 'animate-on-scroll'
                            }`}
                    >
                        {pricingPlans.map((plan, index) => (
                            <PricingCard
                                key={index}
                                name={plan.name}
                                price={plan.price}
                                target={plan.target}
                                employees={plan.employees}
                                modules={plan.modules}
                                highlight={plan.highlight}
                                delay={index * 0.1}
                            />
                        ))}
                    </div>

                    <div className="text-center mt-12 p-6 bg-gray-100 rounded-lg max-w-2xl mx-auto">
                        <h3 className="text-xl font-semibold mb-3">需要客製化方案？</h3>
                        <p className="text-gray-600 mb-4">
                            500名員工以上企業可聯繫我們，提供客製化模組與專屬開發服務。
                        </p>
                        <Link to="/contact" className="btn btn-outline">
                            聯繫業務專員
                        </Link>
                    </div>
                </div>
            </section>

            {/* 客戶見證 */}
            <section className="section bg-gray-50">
                <div className="container">
                    <h2 className="section-title">客戶見證</h2>
                    <p className="section-subtitle">
                        聽聽我們的客戶如何評價智能出勤與人事管理系統
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {testimonials.map((testimonial, index) => (
                            <TestimonialCard
                                key={index}
                                name={testimonial.name}
                                company={testimonial.company}
                                quote={testimonial.quote}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* 呼籲行動區塊 */}
            <section id="contact" className="bg-primary-600 text-white py-16">
                <div className="container text-center">
                    <h2 className="text-3xl font-bold mb-4">立即免費試用</h2>
                    <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
                        提供 14 天免費試用期，無需信用卡，體驗如何透過智能系統提升企業管理效率。
                    </p>
                    <div className="max-w-md mx-auto">
                        <div className="flex flex-col md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                            <input
                                type="email"
                                placeholder="您的電子郵件"
                                className="flex-1 px-4 py-3 rounded-lg text-gray-900"
                            />
                            <button className="px-6 py-3 bg-white text-primary-600 font-medium rounded-lg hover:bg-gray-100">
                                開始使用
                            </button>
                        </div>
                        <p className="mt-4 text-sm opacity-80">
                            或直接致電：0988-261-474
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;